import { readM365AcquisitionsFromCache } from 'owa-m365-acquisitions/lib/readM365AcquisitionsFromCache';
import { writeM365MessageExtensionsQuery } from 'owa-nova-cache/lib/transform/writeM365MessageExtensionsQuery';
import { isMOS3AppServiceAppsInMailAvailable } from 'owa-m365-acquisitions/lib/utils/isMOS3AppServiceAppsInMailAvailable';
import getExtensibilityState from 'owa-addins-store/lib/store/getExtensibilityState';
import isExtensibilityContextInitialized from 'owa-addins-store/lib/store/isExtensibilityContextInitialized';
import { lazyInitializeExtensibilityContext } from 'owa-addins-boot/lib/lazyFunctions';
import { writeM365MessageExtensionsQueryFromEnabledAddins } from 'owa-nova-cache/lib/transform/writeM365MessageExtensionsQueryFromEnabledAddins';
import { writeM365AppsQuery } from 'owa-nova-cache/lib/transform/writeM365AppsQuery';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import { isCapabilityEnabled } from 'owa-capabilities';
import { populateAddinsCapability } from 'owa-capabilities-definitions/lib/populateAddinsCapability';
import type { MailboxInfo } from 'owa-client-types';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isAppBarEnabled } from './isAppBarEnabled';
import { getApplicationSettings } from 'owa-application-settings';
import { trace } from 'owa-trace';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { getFlyoutDebugCorrelationId } from 'owa-addins-store/lib/utils/flyoutDebugCorrelationId';

/**
 * Writes cached acquisitions to Apollo UI queries (appbar, flyouts).
 */
export async function writeAcquisitionQueries(mailboxInfo?: MailboxInfo): Promise<void> {
    // Check if the current mailbox is the still the one that was switched to. It's possible that while fetching acquisitions from MOS3,
    // the user switched to another mailbox. In that case, we don't want to write the flyout queries. If there is no mailboxInfo,
    // multiaccount is disabled so there was no account switch
    if (
        !mailboxInfo ||
        getIndexerValueForMailboxInfo(getModuleContextMailboxInfo()) ===
            getIndexerValueForMailboxInfo(mailboxInfo)
    ) {
        // Fetch apps from cache for account being switched to
        const acquisitions = readM365AcquisitionsFromCache(undefined /*datapoint*/, mailboxInfo);

        // Debug logging for RCA of add-ins disappearing mid-session
        if (getApplicationSettings('Addin')?.isVerboseLogsEnabled) {
            const extState = getExtensibilityState(mailboxInfo);
            const isMOS3 = isMOS3AppServiceAppsInMailAvailable(mailboxInfo);
            const isExtCtxInit = isExtensibilityContextInitialized(mailboxInfo);
            const debugCid = getFlyoutDebugCorrelationId();

            trace.info(
                `[flyout-debug] cid=${debugCid} writeAcquisitionQueries called - ` +
                    `isMOS3: ${isMOS3}, ` +
                    `isExtCtxInit: ${isExtCtxInit}, ` +
                    `acquisitionsCount: ${acquisitions?.length ?? 0}, ` +
                    `enabledAddinCommandsInit: ${extState?.EnabledAddinCommands?.isInitialized}, ` +
                    `composeAddins: ${
                        extState?.EnabledAddinCommands?.getExtensionPoint?.(
                            ExtensibilityModeEnum.MessageCompose
                        )?.length ?? 0
                    }, ` +
                    `readAddins: ${
                        extState?.EnabledAddinCommands?.getExtensionPoint?.(
                            ExtensibilityModeEnum.MessageRead
                        )?.length ?? 0
                    }`,
                'addins'
            );
        }

        if (isAppBarEnabled()) {
            await writeM365AppsQuery(acquisitions, mailboxInfo);
        }

        if (isMOS3AppServiceAppsInMailAvailable(mailboxInfo)) {
            // Write read/compose flyout UI queries from cached acquisitions
            await writeM365MessageExtensionsQuery(acquisitions, mailboxInfo);
        } else if (
            isCapabilityEnabled(
                populateAddinsCapability,
                mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()
            )
        ) {
            // Fetch addins from GEC/store instead of MOS3/apollo cache and write flyout queries using it
            if (!isExtensibilityContextInitialized(mailboxInfo)) {
                await lazyInitializeExtensibilityContext.importAndExecute(mailboxInfo);
            }

            writeM365MessageExtensionsQueryFromEnabledAddins(
                getExtensibilityState(mailboxInfo).EnabledAddinCommands,
                mailboxInfo
            );
        }
    }
}
